import {type MoneyResponseBody} from '@online-offer/shared';
import {useDateTimeFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {always, isNil} from 'ramda';

import {Nullish, parseDate} from 'shared/web';

import {ComparisonValue} from '../../../utils/buildComparisonRows';
import {translateWithFallback as t} from '../../../utils/translateWithFallback';

const KW_TO_HP = 1.35962;
const EMPTY = '—';

export function useFormatComparisonValue() {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const formatDateTime = useDateTimeFormatter();

  const formatMoney = (money: MoneyResponseBody | Nullish, isNegative = false) =>
    isNil(money) || Number(money.amount) === 0
      ? EMPTY
      : `${isNegative ? '− ' : ''}${formatCurrency(Number(money.amount), money.currency)}`;

  return (value: ComparisonValue): string =>
    match(value)
      .with({type: 'money'}, ({money, isNegative}) => formatMoney(money, isNegative))
      .with({type: 'vat'}, ({money}) =>
        // A 0 % VAT (margin scheme) is a real amount, not a missing one.
        isNil(money) ? EMPTY : (formatCurrency(Number(money.amount), money.currency) ?? EMPTY)
      )
      .with({type: 'accessories'}, ({money, names}) =>
        isNil(money) ? EMPTY : `${formatMoney(money)} · ${names.join(', ')}`
      )
      .with({type: 'text'}, ({text}) => text ?? EMPTY)
      .with({type: 'date'}, ({date}) =>
        isNil(date) ? EMPTY : formatDateTime('dateShort', parseDate(date))
      )
      .with({type: 'power'}, ({kw}) =>
        isNil(kw) ? EMPTY : `${formatNumber(kw)} kW / ${formatNumber(Math.round(kw * KW_TO_HP))} HP`
      )
      .with({type: 'mileage'}, ({km}) => (isNil(km) ? EMPTY : `${formatNumber(km)} km`))
      // Only a factory configuration gets its VIN later; a stock vehicle without one has none on record.
      .with({type: 'vin', vin: Pattern.string}, ({vin}) => vin)
      .with({type: 'vin', kind: 'CONFIGURATION'}, () =>
        t('alternatives.comparison.vinAfterOrdering', 'Assigned after ordering')
      )
      .with({type: 'vin'}, always(EMPTY))
      .with({type: 'source'}, ({kind, condition}) =>
        match({kind, condition})
          .with({kind: 'CONFIGURATION'}, () =>
            t('alternatives.source.configuration', 'New vehicle · factory configuration')
          )
          .with({condition: 'USED'}, () =>
            t('alternatives.source.usedStock', 'Used vehicle · stock')
          )
          .otherwise(() => t('alternatives.source.newStock', 'New vehicle · stock'))
      )
      .with({type: 'availability'}, ({availability}) =>
        availability === 'IN_STOCK'
          ? t('alternatives.availability.inStock', 'Available at the dealership')
          : t('alternatives.availability.factoryOrder', 'Factory order · delivery to be confirmed')
      )
      .with({type: 'equipment'}, ({status, packageName, price}) =>
        match(status)
          .with('STANDARD', () => t('alternatives.equipment.standard', 'Standard'))
          .with('OPTION', () =>
            isNil(price)
              ? t('alternatives.equipment.option', 'Included option')
              : `${t('alternatives.equipment.option', 'Included option')} · ${formatMoney(price)}`
          )
          .with('PACKAGE', () =>
            t('alternatives.equipment.package', 'Included in {packageName}', {
              packageName: packageName ?? t('alternatives.equipment.aPackage', 'a package'),
            })
          )
          .with('NOT_LISTED', () => t('alternatives.equipment.notListed', 'Not listed'))
          .exhaustive()
      )
      .exhaustive();
}
